import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { formatUZS } from '@/lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Undo2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiErrorMessage } from '@/lib/api-error';

type Sale = components['schemas']['SaleResource'];

interface Props {
  sale: Sale | null;
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
}

type Mode = 'full' | 'partial' | 'void';

export function RefundDialog({ sale, open, onClose, onDone }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>('full');
  const [reason, setReason] = useState('');
  const [qty, setQty] = useState<Record<number, string>>({});

  const items = sale?.items || [];

  // Real POST /sales/{id}/refund needs sale_item_id (this sale's own line
  // id), not product_id — a sale can have two lines of the same product.
  const partialItems = items
    .map((i) => {
      const q = Number(qty[i.id] || 0);
      if (!q || q <= 0) return null;
      const maxQ = Number(i.quantity);
      const useQ = Math.min(q, maxQ);
      return { sale_item_id: i.id, quantity: useQ };
    })
    .filter((i): i is { sale_item_id: number; quantity: number } => i !== null);

  const partialTotal = partialItems.reduce((s, p) => {
    const item = items.find((i) => i.id === p.sale_item_id);
    return s + (item ? Number(item.unit_price) * p.quantity : 0);
  }, 0);

  const refund = useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST('/sales/{sale}/refund', {
        params: { path: { sale: sale!.id } },
        body: {
          refund_type: mode,
          reason: reason.trim(),
          items: mode === 'partial' ? partialItems : undefined,
        },
      });
      if (error) throw new Error(apiErrorMessage(error, t('misc_refund.op_error')));
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/sales'] });
      queryClient.invalidateQueries({ queryKey: ['/stocks'] });
      toast({
        title: mode === 'void' ? t('misc_refund.voided') : t('misc_refund.done'),
        description: t('misc_refund.stock_restored'),
      });
      setReason('');
      setQty({});
      onClose();
      onDone?.();
    },
    onError: (e: Error) => toast({ title: t('misc_refund.error'), description: e.message, variant: 'destructive' }),
  });

  const disabled =
    refund.isPending ||
    !reason.trim() ||
    (mode === 'partial' && partialTotal <= 0);

  const modes: { key: Mode; label: string; hint: string }[] = [
    { key: 'full', label: t('misc_refund.m_full'), hint: t('misc_refund.m_full_hint') },
    { key: 'partial', label: t('misc_refund.m_partial'), hint: t('misc_refund.m_partial_hint') },
    { key: 'void', label: t('misc_refund.m_void'), hint: t('misc_refund.m_void_hint') },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('misc_refund.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {modes.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMode(m.key)}
                className={`text-left p-3 rounded-xl border transition-colors ${
                  mode === m.key ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent/50'
                }`}
              >
                <p className="text-sm font-medium text-foreground">{m.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{m.hint}</p>
              </button>
            ))}
          </div>

          {mode === 'partial' && (
            <div className="border border-border rounded-xl divide-y divide-border">
              {items.map((i) => (
                <div key={i.id} className="flex items-center gap-3 p-3">
                  <Checkbox
                    checked={!!qty[i.id]}
                    onCheckedChange={(c) =>
                      setQty((prev) => ({ ...prev, [i.id]: c ? String(i.quantity) : '' }))
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{i.product_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {Number(i.quantity)} {i.unit || t('misc_refund.default_unit')} × {formatUZS(Number(i.unit_price))}
                    </p>
                  </div>
                  <Input
                    type="number"
                    min={0}
                    max={Number(i.quantity)}
                    value={qty[i.id] || ''}
                    onChange={(e) => setQty((prev) => ({ ...prev, [i.id]: e.target.value }))}
                    className="w-20 text-sm"
                    placeholder="0"
                  />
                </div>
              ))}
              <div className="flex justify-between p-3 bg-muted/40">
                <span className="text-sm text-muted-foreground">{t('misc_refund.refund_amount')}</span>
                <span className="text-sm font-semibold text-foreground">{formatUZS(partialTotal)}</span>
              </div>
            </div>
          )}

          {mode !== 'partial' && (
            <div className="flex justify-between px-4 py-3 bg-muted/40 rounded-xl">
              <span className="text-sm text-muted-foreground">{t('misc_refund.amount')}</span>
              <span className="text-sm font-semibold text-foreground">
                {formatUZS(Number(sale?.total_amount || 0))}
              </span>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">{t('misc_refund.reason')}</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('misc_refund.reason_ph')}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={refund.isPending}>
            {t('misc_common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => refund.mutate()}
            disabled={disabled}
          >
            {refund.isPending ? (
              <><Loader2 size={16} className="mr-2 animate-spin" /> {t('misc_refund.processing')}</>
            ) : (
              <><Undo2 size={16} className="mr-2" /> {t('misc_refund.confirm')}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
